import { LeadsDatesFilter } from '@gh/builder-shared-data';
import { GridLayout, onlyVisible } from '@gh/core-ui-grid';
import { uniq } from 'lodash';

/** Filter shape used when resolving list projection (office + corp + master lists). */
export type LeadsListFieldsFilter = {
  dateType?: LeadsDatesFilter | string | null;
  showOverdue?: boolean;
  stage?: string;
  priority?: string;
};

const MANDATORY_FIELDS = [
  'contactId',
  'leadId',
  'activeSalesFolderId',
  'isSuppressed',
  'isImported',
  'officeId',
] as const;

/** Grid column id → API / DTO field name (LeadListItemDto). */
const GRID_COLUMN_TO_API_FIELD: Record<string, string> = {
  name: 'name',
  ref: 'ref',
  address: 'address',
  city: 'city',
  state: 'state',
  postcode: 'postcode',
  communityName: 'communityName',
  referredBy: 'referredBy',
  lastContactDate: 'lastContactDate',
  meetingSfStageId: 'meetingSfStageId',
  meetingSfPriorityId: 'meetingSfPriorityId',
  meetingSfStatusId: 'meetingSfStatusId',
  contactEmail: 'contactEmail',
  contactPhone: 'contactPhone',
  createdDate: 'createdDate',
  salesperson: 'salesperson',
  salespersonId: 'salespersonId',
  officeName: 'officeName',
  masterZoneName: 'masterZoneName',
  nextActivity: 'nextActivity',
};

const FIELD_DEPENDENCIES: Record<string, string[]> = {
  meetingSfStatusId: ['notProceedingReason'],
  salesperson: ['salespersonId'],
};

/**
 * Builds the {@code fields} query parameter for GET /crm/leads from visible grid columns.
 */
export function resolveLeadsListFields(
  layout: GridLayout | undefined,
  filter: LeadsListFieldsFilter,
  sortField?: string,
): string[] | undefined {
  if (!layout?.columns?.length) {
    return void 0;
  }

  const fields = new Set<string>(MANDATORY_FIELDS);

  layout.columns.filter(onlyVisible).forEach((column) => {
    const apiField = GRID_COLUMN_TO_API_FIELD[column.column];
    if (apiField) {
      fields.add(apiField);
      (FIELD_DEPENDENCIES[apiField] || []).forEach((dep) => fields.add(dep));
    }
  });

  if (sortField && GRID_COLUMN_TO_API_FIELD[sortField]) {
    fields.add(GRID_COLUMN_TO_API_FIELD[sortField]);
  } else if (sortField) {
    fields.add(sortField);
  } else if (layout.sort?.field) {
    const sorted = GRID_COLUMN_TO_API_FIELD[layout.sort.field] || layout.sort.field;
    fields.add(sorted);
  }

  if (filter.dateType === LeadsDatesFilter.LastContactDate
    || filter.dateType === String(LeadsDatesFilter.LastContactDate)) {
    fields.add('lastContactDate');
  }

  if (filter.showOverdue) {
    fields.add('nextActivity');
  }

  if (filter.stage) {
    fields.add('meetingSfStageId');
  }

  if (filter.priority) {
    fields.add('meetingSfPriorityId');
  }

  return uniq([...fields]);
}
